import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { t, archetypeLabel } from "../lib/i18n";
import { ArrowLeft, GearSix, CrosshairSimple, Diamond } from "@phosphor-icons/react";

export default function DeckDetail() {
  const { id } = useParams();
  const [deck, setDeck] = useState(null);
  const [components, setComponents] = useState([]);
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    api.get(`/decks/${id}`).then((r) => {
      setDeck(r.data);
      if (r.data.owner_player_id)
        api.get(`/players/${r.data.owner_player_id}`).then((rr) => setOwner(rr.data));
    }).catch(() => {});
    api.get("/components").then((r) => setComponents(r.data));
  }, [id]);

  if (!deck)
    return <div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-white/40">{t.loading}</div>;

  const findById = (cid) => components.find((c) => c.id === cid);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <Link to="/decks" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-white">
        <ArrowLeft size={12} /> {t.navDecks}
      </Link>

      <div className="mt-6 border border-white/10 bg-[#0C0C14] p-8 relative overflow-hidden">
        <div className="bg-grid absolute inset-0 opacity-30" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {archetypeLabel(deck.archetype)}
          </div>
          <h1 className="font-display font-black uppercase italic text-6xl sm:text-7xl leading-none mt-2">
            {deck.name}
          </h1>
          <p className="text-white/70 max-w-2xl mt-4">{deck.description}</p>
          {owner && (
            <Link
              to={`/players/${owner.id}`}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#00E5FF] mt-4 hover:underline"
            >
              {t.ownedBy} {owner.handle} →
            </Link>
          )}
        </div>
      </div>

      <h2 className="font-display font-black uppercase italic text-3xl mt-12 mb-6">
        {t.componentLoadout} · {(deck.beys || []).length || 3} {t.bey}
      </h2>

      <div className="grid lg:grid-cols-3 gap-5">
        {Array.from({ length: Math.max((deck.beys || []).length, (deck.blades || []).length, 3) }, (_, i) => i).map((i) => {
          const bey = (deck.beys && deck.beys[i]) || {};
          const blade = findById(bey.blade || (deck.blades || [])[i]);
          const lockchip = findById(bey.lockchip);
          const overBlade = findById(bey.over_blade);
          const assistBlade = findById(bey.assist_blade);
          const ratchet = findById(bey.ratchet || (deck.ratchets || [])[i]);
          const bit = findById(bey.bit || (deck.bits || [])[i]);
          return (
            <div
              key={i}
              data-testid={`bey-slot-${i}`}
              className="border border-white/10 bg-[#0C0C14] relative overflow-hidden"
            >
              <div className="bg-grid absolute inset-0 opacity-20" />
              <div className="relative p-6 border-b border-white/10">
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#FF0055] font-bold">
                  {t.bey} {i + 1}
                </div>
                <div className="font-display font-black uppercase italic text-3xl mt-2">
                  {blade?.name || "—"}
                </div>
              </div>
              <CompRow icon={GearSix} label={t.blade} value={blade?.name} />
              {lockchip && <CompRow icon={Diamond} label={t.lockchip} value={lockchip.name} />}
              {overBlade && <CompRow icon={Diamond} label={t.overblade} value={overBlade.name} />}
              {assistBlade && <CompRow icon={Diamond} label={t.assistBlade} value={assistBlade.name} />}
              <CompRow icon={CrosshairSimple} label={t.ratchet} value={ratchet?.name} />
              <CompRow icon={Diamond} label={t.bit} value={bit?.name} last />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompRow({ icon: Icon, label, value, last }) {
  return (
    <div className={`flex items-center gap-3 px-6 py-4 ${last ? "" : "border-b border-white/5"}`}>
      <Icon size={18} className="text-white/40" />
      <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 w-20">{label}</span>
      <span className="font-semibold text-white">{value || "—"}</span>
    </div>
  );
}
